/** English (base) translations. Keys are grouped by area. */
export const en = {
    seo: {
        "landing": {
            "title": "TMC Studio — Draw any football tactic in 30 seconds",
            "description": "Keyboard-fast football tactics board for coaches, analysts and creators. Build formations, animate plays and export PNG, GIF, PDF or SVG — right in your browser."
        },
        "pricing": {
            "title": "Pricing — TMC Studio",
            "description": "Start free, no account needed. Compare Free, Pro and Team plans. Upgrade to Pro for unlimited projects and GIF/PDF export."
        }
    },
    cookieBanner: {
        "label": "Cookie consent",
        "message": "We use essential cookies to run TMC Studio. With your consent we also use analytics cookies to improve the product.",
        "accept": "Accept all",
        "reject": "Reject non-essential",
        "policy": "Cookie Policy"
    },
    pricingPage: {
        "hero": {
            "title": "Choose how you want to use TMC Studio",
            "subtitle": "Start free, no account needed. Upgrade only when you hit a real limit."
        },
        "billing": {
            "monthly": "Monthly",
            "yearly": "Yearly",
            "yearlyHint": "2 months free"
        },
        "plans": {
            "guestName": "Guest",
            "freeName": "Free",
            "proName": "Pro",
            "teamName": "Team",
            "guestCta": "Open the board",
            "freeCta": "Continue for free",
            "proCta": "Upgrade to Pro",
            "teamCta": "Upgrade to Team",
            "perMonth": "/mo",
            "perYear": "/yr",
            "free": "Free",
            "guestTagline": "No account needed",
            "freeTagline": "Free forever after sign-in",
            "proTagline": "For coaches & creators",
            "teamTagline": "For clubs & staff"
        },
        "matrix": {
            "feature": "Feature",
            "projects": "Projects",
            "steps": "Steps per project",
            "folders": "Folders",
            "cloudSync": "Cloud sync",
            "exportPng": "PNG / JPG export",
            "exportGif": "GIF export",
            "exportPdf": "PDF export",
            "seats": "Seats",
            "invite": "Invite members",
            "unlimited": "Unlimited"
        },
        "vatNote": "Prices shown for EU consumers include VAT. Business customers with a valid EU VAT ID are billed under reverse charge.",
        "faq": {
            "title": "Billing questions",
            "q1": "Is Free really free?",
            "a1": "Yes. Free is not a trial — it stays free forever after you create an account. No card required.",
            "q2": "Can I cancel anytime?",
            "a2": "Yes. Manage or cancel your subscription anytime from your account; you keep Pro until the end of the billing period.",
            "q3": "How do refunds work?",
            "a3": "Digital subscriptions follow our refund policy and EU consumer rights. See the Refunds page for details.",
            "q4": "Do you offer yearly billing?",
            "a4": "Yes. Yearly billing gives you two months free compared to paying monthly."
        },
        "teamCalc": {
            "title": "Perfect for clubs & staff",
            "subtitle": "Team gives every coach their own workspace with shared billing — at a fraction of the cost of separate Pro subscriptions.",
            "fivePro": "5 × Pro subscriptions",
            "teamPlan": "Team plan",
            "savings": "Save {{amount}}{{period}}",
            "cta": "Go Team"
        }
    },
    landing: {
        "nav": {
            "features": "Features",
            "pricing": "Pricing",
            "download": "Download",
            "openBoard": "Open the board"
        },
        "hero": {
            "titlePre": "Draw any football tactic in ",
            "titleHighlight": "30 seconds",
            "subtitle": "Keyboard-fast tactics board for coaches, analysts and creators. Open it in your browser and start drawing — no install, no login.",
            "trustLine": "No account · Free · Runs in your browser",
            "ctaPrimary": "Open the board — no signup",
            "ctaSecondary": "See plans",
            "demoAlt": "Animated demo of building a tactic",
            "badge": "Runs in your browser — no install"
        },
        "credibility": {
            "title": "Everything you need, right away",
            "item1Metric": "30 s",
            "item1Label": "to your first finished tactic",
            "item2Metric": "4 formats",
            "item2Label": "export PNG · GIF · PDF · SVG",
            "item3Metric": "$0",
            "item3Label": "start with no account or card"
        },
        "how": {
            "title": "From idea to tactic in 30 seconds",
            "s1Title": "Pick a formation",
            "s1Desc": "Press 1–6 to drop a full formation on the pitch instantly.",
            "s2Title": "Add movement",
            "s2Desc": "Draw passes and runs with A and R, straight from the keyboard.",
            "s3Title": "Export & share",
            "s3Desc": "Hit Cmd+E for an image, or export an animated GIF or PDF."
        },
        "pillars": {
            "title": "Built for speed",
            "speedTitle": "Fast",
            "speedDesc": "From blank pitch to finished tactic before the kettle boils.",
            "stepsTitle": "Minimum steps",
            "stepsDesc": "No digging through menus. A shortcut for everything, plus a command palette.",
            "everywhereTitle": "No install",
            "everywhereDesc": "Works in any modern browser — Chrome, Safari, Firefox, Edge. Nothing to download.",
            "shareTitle": "Ready to share",
            "shareDesc": "Export PNG, GIF, PDF or SVG in one keystroke."
        },
        "workflow": {
            "eyebrow": "One coaching library",
            "title": "From an idea on the pitch to a complete session plan",
            "desc": "Do more than save an image. Keep the diagram, turn it into a reusable drill and place it in a complete training session with timing, squad, staff and equipment.",
            "graphicTitle": "1. Diagram",
            "graphicDesc": "Draw the setup, movement and decisive zones on a keyboard-fast tactics board.",
            "exerciseTitle": "2. Drill",
            "exerciseDesc": "Add the objective, organization, duration, player count, coaching points and progression.",
            "sessionTitle": "3. Session plan",
            "sessionDesc": "Order the drills, assign staff and squad, then download a PDF or print the plan.",
            "presetTitle": "4. Squad presets",
            "presetDesc": "Save Low block, Mid block and pressing setups. Load them from the bench or with Option/Alt+1–9.",
            "cta": "Build a training session"
        },
        "keyboard": {
            "title": "Keyboard-first, mouse-optional",
            "desc": "Every action has a shortcut, so your hands never leave the keys.",
            "palette": "Press Cmd+K for the command palette."
        },
        "everywhere": {
            "title": "On every device",
            "desc": "Use TMC Studio in any browser, or install the desktop app for macOS and Windows. Everything syncs through your account.",
            "cta": "Open the board"
        },
        "useCases": {
            "title": "Made for your workflow",
            "coachesTitle": "Coaches & analysts",
            "coachesDesc": "Prepare sessions and match briefings, then print or export to PDF.",
            "creatorsTitle": "Content creators",
            "creatorsDesc": "Turn ideas into shareable animated GIFs in under a minute.",
            "clubsTitle": "Clubs & staff",
            "clubsDesc": "Export briefings to PDF and hand them to your whole staff — one consistent plan."
        },
        "pricingTeaser": {
            "title": "Free stays free forever",
            "desc": "Start free, no account needed. Upgrade to Pro when you need unlimited projects and GIF/PDF export.",
            "cta": "Compare plans",
            "free": "Free",
            "pro": "Pro"
        },
        "spotlight": {
            "title": "Built for the way you work",
            "kbTitle": "Keyboard-first. Everything has a shortcut.",
            "kbDesc": "Press Cmd+K to open the command palette, or tap A for arrows, P for players, N for new steps. Your hands never leave the keyboard — drag, resize, and edit without touching menus.",
            "stepsTitle": "Export talking points, not screenshots.",
            "stepsDesc": "Build tactics step by step, then export the whole sequence as an animated GIF or a multi-page PDF. Coaches use it for match prep, creators turn it into social content.",
            "syncTitle": "Open a link. Start drawing.",
            "syncDesc": "No install, no account to begin. Guest mode lets you draw instantly, and a free account saves your projects to the cloud."
        },
        "faq": {
            "title": "Frequently asked questions",
            "q1": "Is it really free?",
            "a1": "Yes. Guest mode requires no account or credit card. A free account includes three graphics, exercises and session plans each with cloud sync — no trial or expiration.",
            "q2": "What can I do as a Guest?",
            "a2": "Start drawing immediately — no account, no signup. You get 1 project with 5 steps and PNG export. Ready to save? Create a free account in one click.",
            "q3": "Do I need to download anything?",
            "a3": "No. TMC Studio works in any modern browser — Chrome, Safari, Firefox, Edge. Just open it and draw, nothing to download.",
            "q4": "What formats can I export?",
            "a4": "Guest and Free: PNG and JPG. Pro adds GIF animations and PDF with all steps. Each format is one click or shortcut away.",
            "q5": "How does Pro billing work?",
            "a5": "Pro is \$9/month or \$90/year (save 17%). You can cancel anytime — access continues until the end of the billing period. All prices include VAT for EU customers.",
            "q6": "Can my whole staff use Team?",
            "a6": "Yes. Team gives 5 seats with individual workspaces and shared billing. Each coach manages their own boards, and you get one invoice.",
            "q7": "Can I save multiple team setups?",
            "a7": "Yes. Save up to 50 named presets such as Low block or Pressing 4-3-3. Load them from the bench or with Option/Alt+1–9 and edit directly on the pitch.",
            "q8": "Can TMC Studio build complete session plans?",
            "a8": "Yes. Combine a graphic with exercise details, sets and rest intervals, then build a session with squad, staff and ordered exercises. Print it or download a PDF."
        },
        "finalCta": {
            "title": "Start drawing in 30 seconds.",
            "desc": "No account, no download, no credit card. Just you and the pitch.",
            "cta": "Open the board — no signup"
        },
        "footer": {
            "tagline": "Fast, professional football tactics board.",
            "rights": "All rights reserved.",
            "legalNotice": "Legal notice",
            "refunds": "Refunds",
            "accessibility": "Accessibility"
        }
    },
    changelog: { title: 'What’s new', description: 'A clear history of how we improve TMC Studio together with the coaching community.', current: 'Current product version: {{version}}', sections: { new: 'New', improved: 'Improved', fixed: 'Fixed' }, releases: {
            v013: { summary: 'This release completes the coach’s daily workflow with fast squad setups, a full path from graphic to session plan, and easier feedback.', lineups: 'Named squad presets with quick selection and shortcuts adapted to macOS and Windows.', feedback: 'A public changelog and bug report form let the community follow progress and shape the product.', library: 'We streamlined Graphic → Exercise → Session with previews, intervals and PDF export.', responsive: 'The squad bench, preset menus, library and workspaces now fit smaller screens more reliably.', returnFlow: 'Squad editing now returns exactly where it started: Settings or the board.', presetMenu: 'The quick preset editor no longer extends beyond the workspace.' },
            v012: { summary: 'We focused on training planning and larger squads so coaches spend less time entering the same information.', sessions: 'Session plans gained default dates, names and squad selection.', intervals: 'Exercises now support sets, work duration and rest intervals.', bulk: 'The library gained multi-select, bulk deletion and faster organization.', roster: 'Squad import supports up to 35 players per team and preserves duplicate shirt numbers.', pdf: 'Fixed blank PDF exports for exercises and complete sessions.', navigation: 'Returning from an editor now opens the correct library view.' },
            v011: { summary: 'The first major step from a tactics board to a complete environment for preparing training materials.', workflow: 'Introduced separate libraries for graphics, exercises and session plans.', seo: 'Added multilingual, indexable product pages.', oauth: 'Made Google sign-in more reliable with a secure PKCE flow.', pdf: 'Fixed printing and export for the first exercise and session views.', authCallback: 'Removed the stalled screen after returning from Google authorization.' }
        } },
    bugReport: { title: 'Report a bug', description: 'Describe a TMC Studio problem and help us improve the product.', community: 'TMC Studio grows together with the coaching community. Every clear report helps us improve the product faster for everyone.', titleLabel: 'Short problem title', descriptionLabel: 'What happened?', stepsLabel: 'How can we reproduce it?', expectedLabel: 'What should have happened?', emailLabel: 'Reply email (optional)', submit: 'Send report', sending: 'Sending…', sentTitle: 'Thank you for the report', sentBody: 'Your report reached the TMC Studio team. You are helping make the product better.', error: 'The report could not be sent. Try again or email support@tacticsmadeclear.store.' },
    common: {
        close: 'Close',
        saving: 'Saving…',
        loading: 'Loading…',
        language: 'Language',
        settings: 'Settings',
    },
    topbar: {
        pitch: 'Pitch',
        pitchTitle: 'Pitch settings',
        export: 'Export',
        focusMode: 'Focus mode',
        themeToDark: 'Switch to dark mode',
        themeToLight: 'Switch to light mode',
        help: 'Help & shortcuts',
        commandPalette: 'Command palette',
        openProjects: 'Open Projects',
        projectName: 'Project name',
        clickRename: 'Click to rename',
        offline: 'Offline',
        saving: 'Saving…',
        saved: 'Saved',
        unsaved: 'Unsaved',
        players: 'Players',
        addPlayers: 'Add players',
        arrows: 'Arrows',
        showArrows: 'Show arrows',
        zones: 'Zones',
        showZones: 'Show zones',
        equipment: 'Equipment',
        showEquipment: 'Show equipment',
        exportBoard: 'Export board',
        presetSquad: 'Preset your squad',
        squadHint: 'Easy drag & drop onto the pitch',
        rectangleZone: 'Rectangle zone',
        polygonZone: 'Polygon zone',
        tallCone: 'Tall cone',
        ballCluster: 'Ball cluster',
        exportPngCurrent: 'PNG — Current step',
        exportPngAll: 'PNG — All steps',
        exportJpgCurrent: 'JPG — Current step',
        exportPdfAll: 'PDF — All steps',
        exportGif: 'GIF Animation',
        proMarked: 'Pro features marked with ⭐',
        account: 'Account',
        planPro: 'Pro',
        planFree: 'Free',
        accountBilling: 'Account & Billing',
        accountEditorOptions: 'Editor options',
        accountPitchSettings: 'Pitch settings',
        accountSquadSettings: 'Squad settings',
        accountYourProfile: 'Your profile',
        signInToSave: 'Sign in — save projects for free',
        signInToSaveDesc: 'Create a free account to save, sync and manage your boards across devices.',
        upgradePro: 'Upgrade to Pro',
        devTestLogin: 'Dev test login',
        devGuest: 'Guest',
        devFree: 'Bez Premium',
        devSolo: 'Solo',
        devTeam: 'Team',
        clearDevData: 'Clear test data',
        logOut: 'Log Out',
        toggleInspector: 'Toggle Inspector',
        signIn: 'Sign In',
        signInDescription: 'Sign in to save and sync your boards',
    },
    contextMenu: {
        header: {
            player: 'Player #{{number}} ({{team}})',
            zone: 'Zone ({{shape}})',
            text: 'Text: "{{preview}}"',
            arrow: '{{type}} Arrow',
            ball: 'Ball',
            equipment: '{{type}}',
        },
        arrow: {
            pass: 'Pass',
            run: 'Run',
            shoot: 'Shot',
            dribble: 'Dribble',
        },
        shape: {
            rectangle: 'Rectangle',
            ellipse: 'Ellipse',
            polygon: 'Polygon',
        },
        selectedCount: '{{count}} elements selected',
        groupSelected: 'Group selected',
        ungroup: 'Ungroup',
        lockElement: 'Lock element',
        unlockElement: 'Unlock element',
        lockSelected: 'Lock selected',
        unlockSelected: 'Unlock selected',
        copy: 'Copy',
        paste: 'Paste',
        selectAll: 'Select all',
        duplicate: 'Duplicate',
        delete: 'Delete',
        resize: 'Resize…',
        addPlayerTeam1: 'Add Team 1 player',
        addPlayerTeam2: 'Add Team 2 player',
        addPlayerTeam3: 'Add Team 3 player',
        addPlayerTeam4: 'Add Team 4 player',
        addBall: 'Add Ball',
        addArrow: 'Add Arrow',
        addZone: 'Add Zone',
        autoNumberingOn: 'Auto-numbering: ON',
        autoNumberingOff: 'Auto-numbering: OFF',
        changeNumber: 'Change number',
        renumberArrows: 'Renumber from 1',
        switchTeam: 'Switch team',
        cycleShape: 'Cycle shape',
        changeColor: 'Change color',
        editText: 'Edit text',
        alignLeft: 'Align left',
        alignCenter: 'Align center',
        alignRight: 'Align right',
        alignJustify: 'Justify',
        editArrowNumber: 'Edit arrow number',
        rotate: 'Rotate',
        bringToFront: 'Bring to front',
        bringForward: 'Bring forward',
        sendBackward: 'Send backward',
        sendToBack: 'Send to back',
    },
    footer: {
        label: 'Footer',
        download: 'Download app',
        privacy: 'Privacy',
        terms: 'Terms',
        cookies: 'Cookies',
        contact: 'Contact',
        changelog: 'What’s new',
        reportBug: 'Report a bug',
        hide: 'Hide footer',
        show: 'Show footer',
        social: {
            x: 'X profile',
            linkedin: 'LinkedIn profile',
            github: 'GitHub profile',
        },
    },
    legacy: {
        toolbar: {
            newBoard: 'New Board',
            new: 'New',
            saveTitle: 'Save (Ctrl+S)',
            save: 'Save',
            load: 'Load',
            addHomePlayer: 'Add Home Player (P)',
            home: 'Home',
            addAwayPlayer: 'Add Away Player',
            away: 'Away',
            addBall: 'Add Ball (B)',
            ball: 'Ball',
            duplicateTitle: 'Duplicate (Ctrl+D)',
            duplicate: 'Duplicate',
            deleteTitle: 'Delete (Del)',
            delete: 'Delete',
            undo: 'Undo (Ctrl+Z)',
            redo: 'Redo (Ctrl+Shift+Z)',
            hint: 'P: Player | B: Ball | Ctrl+D: Duplicate | Ctrl+Z: Undo',
        },
        rightPanel: {
            properties: 'Properties',
            none: 'No element selected',
            noneHint: 'Click on a player or ball to see its properties',
            selected: '{{count}} elements selected',
            multiHint: 'Multiple selection mode. Use Ctrl+D to duplicate or Delete to remove.',
            type: 'Type',
            homePlayer: 'Home Player',
            awayPlayer: 'Away Player',
            ball: 'Ball',
            number: 'Number',
            label: 'Label (optional)',
            labelPlaceholder: 'e.g. Messi',
            position: 'Position',
            shortcuts: 'Shortcuts',
            shiftClick: 'Shift + click = multi-select',
            delDelete: 'Del = delete selection',
        },
    },
    zoom: {
        out: 'Zoom Out (Cmd -)',
        outAria: 'Zoom out',
        in: 'Zoom In (Cmd +)',
        inAria: 'Zoom in',
        value: 'Zoom: {{percent}}%',
        unlock: 'Unlock view (enable zoom/pan)',
        lock: 'Lock view (prevent zoom/pan)',
        unlockAria: 'Unlock viewport',
        lockAria: 'Lock viewport',
        fit: 'Fit',
        fitTitle: 'Fit to View (Shift+1)',
        fitAria: 'Fit to view',
    },
    offline: {
        message: 'You are offline — changes will sync when back online',
    },
    textEdit: {
        hint: 'Enter to save · Shift+Enter for a new line',
    },
    shortcutsHint: {
        press: 'Press',
        forShortcuts: 'for shortcuts',
        forCommands: 'for commands',
    },
    bottomSheet: {
        inspectorPanel: 'Inspector panel',
        closeInspector: 'Close inspector',
        close: 'Close',
    },
    smartBottom: {
        undo: 'Undo (⌘Z)',
        redo: 'Redo (⇧⌘Z)',
        resize: 'Drag to resize the bottom bar',
        collapse: 'Collapse bar',
        expand: 'Expand bar',
        addPlayer: 'Add Player',
        addBall: 'Add Ball',
        drawArrow: 'Draw Arrow',
        allCommands: '⌘K — All commands',
        elements: '{{count}} element(s)',
        editing: 'Editing',
    },
    inspector: {
        props: 'Props',
        layers: 'Layers',
        identity: 'Identity',
        appearance: 'Appearance',
        labels: 'Labels',
        role: 'Role',
        advanced: 'Advanced',
        numbering: 'Numbering',
        sequence: 'Sequence',
        number: 'Number',
        label: 'Label',
        showLabelBelow: 'Show label below',
        goalkeeper: 'Goalkeeper',
        gkHint: 'Uses team GK color · Shift+G',
        labelSize: 'Label size',
        opacity: 'Opacity',
        opacityAll: 'Opacity (all)',
        playerLabelPlaceholder: 'Surname or nickname…',
        playerLabelAria: 'Player label',
        showOrientation: 'Show orientation',
        showArms: 'Show arms',
        visionCone: 'Vision cone',
        zoomThreshold: 'Zoom threshold',
        showNumber: 'Show number',
        autoNumber: 'Auto-number arrows',
        renumber: 'Renumber 1…N',
        showLabelBelowAria: 'Show label below',
        goalkeeperAria: 'Goalkeeper',
        showOrientationAria: 'Show orientation',
        showArmsAria: 'Show arms',
        visionConeAria: 'Vision cone',
        showNumberAria: 'Show number',
        autoNumberAria: 'Auto-number arrows',
        arrowNumberPlaceholder: 'No number',
        arrowNumberAria: 'Arrow number',
        renumberAria: 'Renumber arrows',
        groups: 'Groups',
        doubleClickRename: 'Double-click to rename',
        unlockGroup: 'Unlock group',
        lockGroup: 'Lock group',
        hideGroup: 'Hide group',
        showGroup: 'Show group',
        locked: 'Lock',
        lockToggle: 'Lock element',
        lockHint: 'Locked elements stay selected, but cannot be moved or resized.',
        visionConeHint: 'V / Shift+V',
        selected: '{{count}} elements selected',
        multiHint: 'Applies to selected players.',
        showLabels: 'Show labels',
        hideLabels: 'Hide labels',
        searchPlaceholder: 'Search elements…',
        noElements: 'No elements found',
        quickActions: 'Quick Actions',
        commandPalette: 'Command Palette',
        closeInspector: 'Close Inspector (I)',
        closeInspectorAria: 'Close inspector',
        resize: 'Drag to resize panel',
        openInspector: 'Open Inspector (I)',
        openInspectorAria: 'Open inspector',
        openInspectorPanelAria: 'Open inspector panel',
        inspectorTitle: 'Inspector (I)',
        showCoordinates: 'Show coordinates',
        elementType: {
            player: 'Player', ball: 'Ball', arrow: 'Arrow', zone: 'Zone', text: 'Text',
        },
        team: {
            home: 'Home', away: 'Away', team3: 'Team 3', team4: 'Team 4',
        },
        setAsDefault: 'Set as default', setAsDefaultHint: 'Save the current look as the default for new elements', setAsDefaultDone: 'Saved as default',
        zone: {
            border: 'Border', borderStyle: 'Style', solid: 'Solid', dashed: 'Dashed', none: 'None',
            borderWidth: 'Width', borderColor: 'Color', corners: 'Corner markers',
        },
        arrow: {
            head: 'Arrow Head', startHead: 'Start', endHead: 'End',
            line: 'Line', thickness: 'Thickness', color: 'Color',
            headArrow: 'Arrow', headNone: 'None', headBar: 'Bar', headDot: 'Dot',
            doubleHead: 'Double head', hideHeads: 'Hide heads',
        },
        filters: {
            all: 'All', home: 'Home', away: 'Away', ball: 'Ball',
        },
        layerNames: {
            homePlayers: 'Home Players', awayPlayers: 'Away Players', ball: 'Ball',
            arrows: 'Arrows', zones: 'Zones', labels: 'Text & Labels',
            equipment: 'Equipment', drawings: 'Drawings',
        },
    },
    settings: {
        account: 'Account',
        club: 'Club & Members',
        editor: 'Editor',
        board: 'Board',
        general: 'General',
        profile: 'Profile',
        security: 'Security',
        billing: 'Billing',
        preferences: 'Preferences',
        squad: 'Squad',
        teams: 'Teams',
        pitch: 'Pitch',
        shortcuts: 'Shortcuts',
        dataPrivacy: 'Data & privacy',
        about: 'About',
        close: 'Close settings',
        profileInfo: 'Profile Information',
        profilePhoto: 'Profile photo',
        uploading: 'Uploading…',
        uploadPhoto: 'Upload new photo',
        fullName: 'Full Name',
        yourName: 'Your name',
        emailReadonly: 'Email cannot be changed',
        saving: 'Saving…',
        saveChanges: 'Save Changes',
        changePassword: 'Change Password',
        currentPassword: 'Current Password',
        currentPasswordPlaceholder: 'Enter current password',
        newPassword: 'New Password',
        newPasswordPlaceholder: 'Enter new password (min 8 characters)',
        confirmNewPassword: 'Confirm New Password',
        confirmNewPasswordPlaceholder: 'Confirm new password',
        changing: 'Changing…',
        dangerZone: 'Danger Zone',
        deleteAccount: 'Delete Account',
        deleteWarning: 'This cannot be undone. Your account, projects, files and club are deleted and any subscription is cancelled.',
        typeDelete: 'Type DELETE to confirm',
        enterPassword: 'Enter your password',
        yourPassword: 'Your password',
        deleting: 'Deleting…',
        deleteMyAccount: 'Delete My Account',
        currentPlan: 'Current Plan',
        plan: 'Plan',
        upgrade: 'Upgrade',
        renewsOn: 'Renews on {{date}}',
        freeNudgeTitle: "You're on Free — Upgrade to Pro for more",
        freeNudgeText: 'Get unlimited projects, unlimited steps, GIF & PDF export, and priority support',
        upgradeProArrow: 'Upgrade to Pro →',
        manageSubscription: 'Manage Subscription',
        openBillingPortal: 'Open Billing Portal',
        billingPortalHint: 'Update payment method, view invoices, or cancel subscription',
        appearance: 'Appearance',
        theme: 'Theme',
        light: 'Light',
        dark: 'Dark',
        system: 'System',
        showGrid: 'Show grid',
        showGridHint: 'Display alignment grid on canvas',
        snapGrid: 'Snap to grid',
        snapGridHint: 'Automatically align elements to grid',
        gridDensity: 'Grid density',
        gridDensityHint: 'Controls visible grid spacing and snap spacing for new or moved elements.',
        pixelsValue: '{{value}}px',
        editorDefaults: 'Editor defaults',
        defaultArrow: 'Default arrow',
        defaultArrowHint: 'Used by generic Add Arrow buttons in empty states and quick bars.',
        arrowPass: 'Pass',
        arrowRun: 'Run',
        arrowShoot: 'Shoot',
        arrowDribble: 'Dribble',
        defaultStepDuration: 'Default step duration',
        defaultStepDurationHint: 'Used for playback timing and animated export timing.',
        secondsValue: '{{value}}s',
        styleDefaults: 'Element style defaults', resetDefaults: 'Reset to defaults', arrowDefaults: 'Arrows', zoneDefaults: 'Zones', zoneFill: 'Fill', zoneOpacity: 'Opacity',
        preferencesHint: 'Preferences are synced across your devices via cloud.',
        coachingProfile: 'Coaching profile', coachingProfileHint: 'Club and staff details used in session plans.', clubLogo: 'Club logo', changeLogo: 'Change logo', clubName: 'Club name', clubNamePlaceholder: 'e.g. TMC Academy', logoHint: 'The logo appears in session plans. PNG, JPG or WebP up to 1 MB.', staffPresets: 'Your staff', staffPresetsHint: 'Add staff members to a session plan with one click.', staffRoleFallback: 'Staff', removeStaff: 'Remove {{name}} from staff', staffName: 'Full name', staffRole: 'Role, e.g. assistant', addStaff: 'Add', sessionDefaults: 'New session defaults', sessionDefaultsHint: 'Set the date, name and venue once for every new session plan.', sessionDateOffset: 'Days from today', sessionNameTemplate: 'Name template', sessionNameTemplatePlaceholder: 'Training - {date}', sessionVenue: 'Venue', sessionStartTime: 'Start time', sessionMicrocycleDay: 'Microcycle day', positionGroups: 'Player groups', positionGroupsHint: 'Assign squad players to groups automatically added to each session.', positionGroupPlaceholder: 'e.g. Midfielders', addPositionGroup: 'Add group', removePositionGroup: 'Remove group {{name}}', positionGroupsEmptySquad: 'Add players in the Squad tab first.', teamColors: 'Team colours',
        squadRoster: 'Squad Roster',
        playersCount: '{{count}}/{{max}} players',
        showOnBoard: 'Show on board',
        showSquadOnBoard: 'Show squad on board',
        proFeature: 'Pro Feature',
        squadFreeHint: 'Free plan allows up to 5 squad players. Pro and Club Premium allow up to 35 per team (max 140 total).',
        squadFull: 'The selected team is full. Pro and Club Premium allow 35 players per team.',
        name: 'Name',
        playerName: 'Player name',
        numberShort: '#',
        numberPlaceholder: 'No.',
        team: 'Team',
        add: 'Add',
        addMany: 'Add many',
        addPlayers: 'Add players',
        bulkPlayersLabel: 'Paste a player list',
        bulkPlayersPlaceholder: '1 John Smith\n7, Peter Brown\nAdam Green',
        bulkPlayersHint: 'One player per line. Supplied numbers, including duplicates, are preserved; missing numbers use the first available slot.',
        bulkNoValidPlayers: 'No valid players found to add.',
        bulkSkippedLines: 'Skipped lines with an invalid name or number: {{lines}}',
        editPlayerName: 'Edit {{name}} name',
        editPlayerNumber: 'Edit {{name}} number',
        editPlayerTeam: 'Edit {{name}} team',
        noPlayers: 'No players defined yet. Add your squad above.',
        removePlayer: 'Remove {{name}}',
        squadTip: 'Tip: After adding players, make sure "Show on board" is enabled above. You will see the squad bench below the pitch. Drag any player onto the pitch to place them.',
        languageHint: 'Choose the application language.',
        keyboardShortcuts: 'Keyboard shortcuts',
        shortcutCustomizationHint: 'Shortcuts are grouped by editor workflow. Pro and Club Pro can click a shortcut and record a custom combo.',
        resetShortcuts: 'Reset to factory',
        shortcutResetSuccess: 'Keyboard shortcuts restored to the factory layout.',
        shortcutPressKeys: 'Press keys',
        shortcutSaved: 'Shortcut saved: {{shortcut}}',
        shortcutDuplicate: '{{shortcut}} is already assigned to: {{action}}',
        shortcutProOnly: 'Shortcut editing is available in Pro and Club Pro.',
        editShortcutAria: 'Edit shortcut: {{action}}',
        aboutVersion: 'Version {{version}} — Tactics Made Clear',
        aboutBy: 'by Sportpredictor',
        contact: 'Contact',
        reportBug: 'Report bug',
        sendFeedback: 'Send feedback',
        contactFounder: 'Krystian Rubajczyk',
        contactFounderHint: 'Product contact and quick reports via email or X.',
        legalPrivacy: 'Privacy',
        legalTerms: 'Terms',
        legalCookies: 'Cookies',
        legalInfo: 'Legal info',
        rights: 'All rights reserved.',
        dataHint: 'Export your board to a file, or import one you saved earlier.',
        exportBoard: 'Export board (.json)',
        importBoard: 'Import board',
        importSuccess: 'Board imported successfully.',
        importInvalid: 'Invalid board file.',
        manualSaveSuccess: 'Project saved.',
        manualSaveError: 'The project could not be saved to the cloud. Try again.',
        importWarning: 'Importing replaces the current board — export first if you want to keep it.',
        errors: {
            nameRequired: 'Name is required',
            profileFailed: 'Failed to update profile',
            passwordFields: 'Please fill in all password fields',
            passwordLength: 'New password must be at least 8 characters',
            passwordsMismatch: 'Passwords do not match',
            passwordFailed: 'Failed to change password',
            typeDelete: 'Please type DELETE to confirm',
            passwordRequired: 'Password is required',
            deleteFailed: 'Failed to delete account', ownsClubWithMembers: 'You own a club with other members. Transfer ownership or remove the members first, then delete your account.',
            imageFile: 'Please select an image file',
            imageSize: 'Image must be smaller than 2MB',
            avatarFailed: 'Failed to upload avatar',
        },
        success: {
            profile: 'Profile updated successfully!',
            password: 'Password changed successfully!',
            avatar: 'Avatar updated successfully!',
        },
        shortcutItems: {
            players: 'Add home / away player',
            playersHome: 'Add home player',
            playersAway: 'Add away player',
            ball: 'Add ball',
            arrows: 'Pass / Run / Shot / Dribble arrow',
            passArrow: 'Pass arrow',
            runArrow: 'Run arrow',
            shootArrow: 'Shot arrow',
            dribbleArrow: 'Dribble arrow',
            zones: 'Rectangle / Ellipse zone',
            zoneRect: 'Rectangle zone',
            zoneEllipse: 'Ellipse zone',
            text: 'Text label',
            equipment: 'Cone / Hoop',
            cone: 'Cone',
            hoop: 'Hoop',
            goalkeeper: 'Set goalkeeper / cycle GK color',
            vision: 'Enable vision for selected players',
            orientationHandles: 'Toggle player orientation handles',
            resetOrientation: 'Reset selected player orientation',
            formations: 'Apply home / away formation',
            focus: 'Focus mode',
            fullShortcuts: 'Open full shortcuts',
            resizeUp: 'Increase selected elements',
            resizeDown: 'Decrease selected elements',
        },
        shortcutGroups: {
            create: 'Create elements',
            players: 'Players and orientation',
            workflow: 'Workflow',
            selection: 'Selection',
        },
        savedLineups: 'Tactical lineup library',
        savedLineupsHint: 'Position players on the pitch, name the organization and assign shortcuts {{shortcuts}}.',
        lineupNamePlaceholder: 'e.g. Low block, Mid block, Pressing 4-3-3',
        lineupTeam: 'Lineup team',
        saveCurrentLineup: 'Save current positions',
        noSavedLineups: 'No saved lineups yet. Position players on the pitch and save your first setup.',
        lineupPlayersCount: '{{count}} players',
        lineupShortcut: 'Shortcut for {{name}}',
        noLineupShortcut: 'No shortcut',
        saveName: 'Save',
        editOnBoard: 'Edit on pitch',
        overwriteLineup: 'Overwrite positions',
        renameLineup: 'Rename',
        removeLineup: 'Delete',
        editingLineup: 'Editing lineup',
        editingLineupHint: 'Move players on the pitch, then save your changes.',
        saveLineupChanges: 'Save changes',
        cancelLineupEdit: 'Cancel',
        lineupChangesSaved: 'Changes to {{name}} saved.',
        lineupEditCancelled: 'Lineup editing cancelled.',
        emptySlot: 'Empty slot',
        saveTeam1: 'Save T1',
        saveTeam2: 'Save T2',
        applyLineup: 'Apply',
        lineupSaved: 'Lineup saved in slot {{slot}}.',
        lineupApplied: 'Lineup {{slot}} applied.',
        lineupNeedsPlayers: 'Position players from the selected team on the pitch first.',
        lineupEmpty: 'This lineup is empty.',
    },
    emptyState: {
        title: 'Start your board',
        subtitle: 'Add elements to begin creating your tactical board',
        addPlayer: 'Add Player',
        addPlayerHint: 'Place a player on the pitch',
        addBall: 'Add Ball',
        addBallHint: 'Place the ball on the pitch',
        addArrow: 'Add Arrow',
        addArrowHint: 'Draw a pass or run',
        commandPalette: 'Command Palette',
    },
    auth: {
        continueFree: 'Continue for free',
        resetPassword: 'Reset Password',
        signInSubtitle: 'Sign in to save your work and unlock the Free plan.',
        resetSubtitle: 'Enter your email to reset password',
        requiredFields: 'Please fill in all required fields',
        passwordsMismatch: 'Passwords do not match',
        passwordTooShort: 'Password must be at least 8 characters',
        verifyEmail: 'Account created. Open the confirmation link we sent you. Check spam if it is not in your inbox.',
        devLogin: 'Dev test login (bypass auth)',
        freeTier: 'Bez Premium',
        proTier: 'Solo Premium',
        teamTier: 'Team Premium',
        continueGoogle: 'Continue with Google',
        googlePopupTitle: 'Signing in with Google',
        googlePopupDescription: 'Finish sign-in in the opened window. You can keep working in TMC Studio.',
        googleProgressTitle: 'Finish sign-in in Google',
        googleProgressDescription: 'A Google window is open. After you approve access, TMC Studio will return here automatically.',
        googleProgressWaiting: 'Waiting for Google...',
        popupRecoverySuccessTitle: 'Login finished in this window',
        popupRecoveryErrorTitle: 'Login could not finish',
        popupRecoveryDescription: 'Return to the main TMC Studio window. If it did not update, close this window and try again.',
        popupRecoveryClose: 'Close window',
        or: 'or',
        fullName: 'Full Name',
        fullNamePlaceholder: 'John Doe',
        email: 'Email',
        emailPlaceholder: 'you@example.com',
        password: 'Password',
        confirmPassword: 'Confirm Password',
        forgotPassword: 'Forgot password?',
        loading: 'Loading…',
        signIn: 'Sign In',
        createAccount: 'Create Account',
        sendResetLink: 'Send Reset Link',
        noAccount: "Don't have an account?",
        signUp: 'Sign up',
        alreadyAccount: 'Already have an account?',
        backToSignIn: 'Back to sign in',
        freeForever: 'Free forever. No credit card required.',
        errorInitFailed: 'Failed to initialize authentication',
        errorOfflineMode: 'Authentication is not available in offline mode',
        resetEmailSent: 'We sent a password reset link. Check your inbox and spam folder.',
        errorEmailNotConfirmed: 'Please confirm your email before signing in. Check your inbox.',
        emailNotConfirmed: 'Your email has not been confirmed yet. Check your inbox for the confirmation link.',
        resendConfirmation: 'Resend confirmation email',
        confirmationResent: 'We sent a new confirmation link. Check your inbox and spam folder.',
        newPassword: 'New Password',
        confirmNewPassword: 'Confirm New Password',
        resetPasswordTitle: 'Reset Password',
        resetPasswordSubtitle: 'Enter your new password',
        resetPasswordSuccess: 'Password has been reset successfully. Redirecting to the app…',
        resetPasswordVerifying: 'Verifying reset link…',
        resetPasswordInvalid: 'Password reset link is invalid or expired. Please request a new one.',
        resetPasswordEnterNew: 'Please enter a new password',
        resetPasswordButton: 'Reset Password',
        resettingPassword: 'Resetting…',
        resetPasswordBackHome: '← Back to home',
        authNotConfigured: 'Authentication is not configured',
        resetFailed: 'Failed to reset password',
        callbackLoading: 'Signing in…',
        callbackTimeout: 'Sign-in took too long. Please try again.',
        callbackUnavailable: 'Sign-in is currently unavailable.',
        callbackSessionFailed: 'We could not create your session. Please try again.',
        callbackFailed: 'Sign-in failed. Please try again.',
        callbackErrorTitle: 'Sign-in failed',
        callbackErrorDescription: 'We could not finish signing you in.',
        callbackBack: 'Back to TMC Studio',
    },
    confirm: {
        confirm: 'Confirm',
        cancel: 'Cancel',
        processing: 'Processing…',
    },
    exportToast: {
        canvasNotReady: 'Canvas not ready',
        pngExported: 'PNG exported.',
        exportingSteps: 'Exporting {{count}} steps…',
        exportedPngs: 'Exported {{count}} PNGs.',
        gifPro: 'GIF export is a Pro feature',
        gifNeedSteps: 'Need at least 2 steps for GIF',
        gifCreating: 'Creating GIF…',
        gifEncoding: 'Encoding GIF…',
        gifExported: 'GIF exported.',
        gifFailed: 'GIF export failed',
        pdfPro: 'PDF export is a Pro feature',
        pdfCreating: 'Creating PDF…',
        pdfExported: 'PDF exported.',
        pdfFailed: 'PDF export failed',
        svgExported: 'SVG exported.',
        svgFailed: 'SVG export failed',
        jpgExported: 'JPG exported.',
    },
    billingToast: {
        signInFirst: 'Please sign in first',
        portalFailed: 'Failed to open billing portal',
    },
    settingsToast: {
        profileUpdated: 'Profile updated',
        passwordChanged: 'Password changed',
        accountDeleted: 'Account deleted. Goodbye.',
    },
    projectToast: {
        loaded: 'Project loaded',
        loadFailed: 'Failed to load project',
        limitFree: 'You have {{count}}/3 items of this type. Upgrade to Pro for unlimited.',
        created: 'New project created',
        savedCloud: 'Project saved to cloud',
        saveCloudFailed: 'Failed to save to cloud',
        cloudSaveError: 'Cloud save error. Check console.',
        deleted: 'Project deleted',
        deletedMany: 'Deleted {{count}} projects',
        deletedManyPartial: 'Deleted {{count}} of {{total}} projects',
        deleteFailed: 'Failed to delete project',
        duplicated: 'Project duplicated',
        renamed: 'Project renamed',
        refreshed: 'Projects refreshed',
        graphicAttached: 'Graphic "{{name}}" was added to the exercise',
        folderCreated: 'Folder "{{name}}" created',
        folderCreateFailed: 'Failed to create folder',
        folderUpdated: 'Folder "{{name}}" updated',
        folderUpdateFailed: 'Failed to update folder',
        deleteFolderTitle: 'Delete Folder?',
        deleteFolderDescription: 'This will delete the folder and any subfolders inside it. Your projects will not be deleted — they will remain in your workspace.',
        deleteFolderConfirm: 'Delete Folder',
        folderDeleted: 'Folder deleted',
        folderDeleteFailed: 'Failed to delete folder',
        favoriteAdded: 'Added to favorites',
        favoriteRemoved: 'Removed from favorites',
        favoriteFailed: 'Failed to update favorite',
        movedToFolder: 'Project moved to folder',
        removedFromFolder: 'Project removed from folder',
        moveProjectFailed: 'Failed to move project',
        tagsFailed: 'Failed to save tags',
        projectPinned: 'Project pinned',
        projectUnpinned: 'Project unpinned',
        pinProjectFailed: 'Failed to update pin',
        folderPinned: 'Folder pinned',
        folderUnpinned: 'Folder unpinned',
        pinFolderFailed: 'Failed to update folder pin',
        renameProjectFailed: 'Failed to rename project',
        folderMoved: 'Folder moved',
        folderMoveFailed: 'Failed to move folder',
        folderRenamed: 'Folder renamed',
        renameFolderFailed: 'Failed to rename folder',
    },
    limits: {
        guestTitle: "You've reached the Guest limit",
        freeTitle: 'Free plan limit reached',
        guestOne: 'You already have {{count}} {{resource}} — Guest mode allows only one.',
        guestMany: "You've added {{count}} {{resource}} — that's the Guest limit.",
        freeOne: 'You already have {{count}} {{resource}} — Free plan allows {{max}}.',
        freeMany: "You've created {{count}} {{resource}} — that's the Free plan limit.",
        guestDescription: "You're using TMC Studio without an account. Create a free account to keep building and save your work.",
        freeDescription: "You've reached the limit of the Free plan. Upgrade to Pro to continue without restrictions.",
        guestBenefits: '3 graphics, exercises and session plans each|10 steps per project|Cloud sync & backup|PNG export',
        proBenefits: 'Unlimited projects|Unlimited steps|GIF & PDF export|Priority support',
        continueFree: 'Continue for free',
        upgradePro: 'Upgrade to Pro',
        guestUsage: 'Guest usage',
        freePlan: 'Free plan',
        whatYouGet: 'What you get:',
        comparePlans: 'Compare plans',
        reassurance: 'Free stays free forever. No credit card required.',
        steps: 'steps',
        step: 'step',
        projects: 'projects',
        project: 'project',
    },
    folders: {
        createTitle: 'Create Folder',
        optionsTitle: 'Folder Options',
        name: 'Folder Name',
        placeholder: 'e.g. Tactics, Training Drills…',
        color: 'Folder Color',
        cancel: 'Cancel',
        create: 'Create Folder',
        save: 'Save',
    },
    coaching: {
        library: 'Library', projectName: 'Project name', saving: 'Saving…', saved: 'Saved', saveError: 'Save error', help: 'Quick guide', quickStart: 'Quick start', start: 'Start working', backToBoard: 'Back to board',
        exercise: {
            workspaceTitle: 'Exercise editor', nameLabel: 'Exercise name', graphic: 'Tactical graphic', editGraphic: 'Edit graphic on board', editGraphicShort: 'Edit', drawOnBoard: 'Back to board and draw', openBoard: 'Back to board and draw', backToExercise: 'Finish graphic editing', noGraphic: 'Choose a saved graphic or open the board editor to draw one.', chooseGraphic: 'Choose graphic from library', downloadPdf: 'Download PDF', print: 'Print', pdfError: 'We could not create the PDF. Please try again.', duration: 'Total duration (min)', players: 'Players', intervals: 'Sets and rest', intervalsHint: 'Optionally plan e.g. 2 × 7 min with a one-minute break.', clearIntervals: 'Clear', sets: 'Sets', workMinutes: 'Work (min)', restMinutes: 'Rest (min)', category: 'Category / phase', categoryPlaceholder: 'e.g. warm-up, pressing, build-up', objective: 'Training objective', objectivePlaceholder: 'What should the players learn?', description: 'Exercise content', descriptionPlaceholder: 'Describe the rules and flow of the exercise.', organization: 'Organization', coachingPoints: 'Coaching points', progression: 'Progression / regressions', equipment: 'Equipment',
            guide: { title: 'Create a complete exercise', 1: { title: 'Add the graphic', body: 'Choose an existing drawing or enter the board editor and prepare a new setup.' }, 2: { title: 'Describe the work', body: 'Set the objective, organization, duration, number of players and equipment.' }, 3: { title: 'Save and reuse', body: 'The exercise is saved automatically and becomes available in every session plan.' } },
        },
        session: {
            loadPresetGroups: 'Load groups',
            exportPdf: 'Download PDF', nameLabel: 'Session name', downloadPdf: 'Download PDF', downloadingPdf: 'Creating PDF…', print: 'Print', pdfError: 'We could not create the PDF. Please try again.', eyebrow: 'Training session plan', totalTime: 'Total duration', date: 'Date', microcycleDay: 'Microcycle day', venue: 'Venue', startTime: 'Start time', staff: 'Staff', objectives: 'Session objectives', microcycle: 'Microcycle', mesocycle: 'Mesocycle', playerCount: 'Players', exerciseLibrary: 'Exercise library', exerciseLibraryHint: 'Add saved exercises directly to the session.', searchExercises: 'Search exercises…', noExercises: 'No matching exercises.', trainingContent: 'Training content', emptyPlan: 'Add the first exercise from the library on the left.', exerciseNotes: 'Content, rules, variants and coaching notes…', squad: 'Squad', newGroup: 'New group', addGroup: 'Add position group', playersPlaceholder: 'One player per line', workOrganization: 'Staff organization', addStaff: 'Add staff member', coach: 'Coach', beforeTraining: 'Before training', exerciseShort: 'Ex.', organizationNotes: 'Organization of equipment / spaces', equipmentNotes: 'Equipment and preparation', lineupNotes: 'Lineups and additional notes',
            guide: { title: 'Build a training session plan', 1: { title: 'Complete session data', body: 'Set the date, MD, venue, staff, objectives, workload and squad.' }, 2: { title: 'Add exercises', body: 'Use the exercise library, set the order, duration and instructions for every block.' }, 3: { title: 'Organize and export', body: 'Assign staff responsibilities, add equipment notes, then print or save the plan as PDF.' } },
        },
    },
    pricing: {
        title: 'Choose how you want to use TMC Studio',
        subtitle: 'Start free. Upgrade only if you need more.',
        mostPopular: 'Most Popular',
        currentPlan: 'Current Plan',
        billing: {
            monthly: 'Monthly',
            yearly: 'Yearly',
            perMonth: '/mo',
            perYear: '/yr',
            yearlyHint: '2 months free',
        },
        processing: 'Processing…',
        signInHint: 'Sign in with Google or email',
        footer: 'You can use TMC Studio for free forever.',
        footerSecond: 'Upgrade only when you hit a real limit.',
        checkoutFailed: 'Checkout failed. Please try again.',
        checkoutSessionFailed: 'Failed to create checkout session',
        genericError: 'Something went wrong',
        plans: {
            free: {
                name: 'Free',
                price: 'Included',
                period: ' after sign-in',
                microcopy: 'This is the default plan after login',
                features: '3 graphics, exercises and session plans each|Cloud sync & backup|PNG export|Organize with folders',
                cta: 'Continue for free',
            },
            pro: {
                name: 'Pro',
                price: '$9',
                period: '/month',
                microcopy: 'For coaches who create a lot of drills and exports',
                features: 'Unlimited projects|GIF & PDF export|Unlimited steps|Priority support',
                cta: 'Upgrade to Pro',
            },
            team: {
                name: 'Team',
                price: '$29',
                period: '/month',
                microcopy: 'Pro + invite your staff with a secure link',
                features: '5 team members|Shared billing|Individual workspaces|Everything in Pro',
                cta: 'Upgrade to Team',
            },
        },
    },
    projects: {
        title: 'Projects',
        back: 'Back',
        recentProjects: 'Recent projects',
        recentHint: 'Continue where you left off',
        refresh: 'Refresh projects',
        close: 'Close drawer',
        newProject: 'New Project',
        defaultExerciseName: 'New exercise',
        defaultSessionName: 'New session plan',
        defaultSessionNameWithDate: 'Training - {date}',
        exerciseFromGraphicName: 'Exercise — {{name}}',
        kind: 'Type',
        folder: 'Folder',
        noFolder: 'No folder',
        tags: 'Tags',
        addTag: 'Add a tag and press Enter',
        removeTag: 'Remove tag {{tag}}',
        organize: 'Folder and tags',
        organizeTitle: 'Folder and tags: {{name}}',
        done: 'Done',
        filterByType: 'Filter by type',
        descriptionPlaceholder: 'Objective, organization, flow and key coaching points…',
        tutorial: {
            help: 'How it works', open: 'Library tutorial', close: 'Close tutorial', step: 'Step {{current}} of 3', back: 'Back', next: 'Next', done: 'Done',
            items: {
                0: { title: '1. Draw a graphic', body: 'Save the setup on your board as a Graphic. It becomes the visual base for an exercise.' },
                1: { title: '2. Build an exercise', body: 'Create an exercise from the current graphic or choose one from the library. Add duration, players, organization and coaching points.' },
                2: { title: '3. Assemble a session', body: 'Add completed exercises to a session plan, set their order, duration and notes. Total time is calculated automatically.' },
            },
        },
        exercise: {
            createFromThisGraphic: 'Create exercise from this graphic', graphic: 'Exercise graphic', noGraphics: 'Create a graphic first', useGraphic: 'Use graphic', refreshGraphic: 'Refresh from graphic', usingGraphic: 'Using graphic: {{name}}', duration: 'Duration (min)', players: 'Players', playersPlaceholder: 'e.g. 8 + 2 goalkeepers', organization: 'Organization and flow', coachingPoints: 'Key coaching points',
        },
        session: {
            agenda: 'Exercise plan', total: 'Total: {{minutes}} min', noExercises: 'Create an exercise first', add: 'Add', empty: 'Add the first exercise to this session plan.', moveUp: 'Move up', moveDown: 'Move down', remove: 'Remove from session', itemDuration: 'Exercise duration in minutes', notes: 'Notes for this exercise',
        },
        type: { all: 'All', graphic: 'Graphic', exercise: 'Exercise', session: 'Session plan' },
        search: 'Search projects…',
        clearSearch: 'Clear search',
        sortBy: 'Sort by:',
        results: '{{count}} result(s)',
        pinned: 'Pinned',
        folders: 'Folders',
        newFolder: 'New Folder',
        newSubfolder: 'New Subfolder',
        allProjects: 'All Projects',
        unsorted: 'Unsorted', selectProject: 'Select {{name}}', unselectProject: 'Unselect {{name}}', selectedCount: 'Selected: {{count}}', selectAll: 'Select visible', clearSelection: 'Clear', deleteSelected: 'Delete selected', deleteSelectedTitle: 'Delete {{count}} projects?', deleteSelectedDescription: 'Selected projects will be permanently deleted. Session plans retain saved exercise snapshots.',
        moveToRoot: 'Move to root',
        noProjectsYet: 'No projects yet',
        createProject: 'Create Project',
        signInSync: 'Sign in to sync projects',
        authRequired: 'Cloud projects require authentication',
        signIn: 'Sign In',
        createFirst: 'Create your first project to get started',
        cloudSync: 'Cloud sync enabled',
        signInCloud: 'Sign in to sync to cloud',
        current: 'Current',
        projectName: 'Project name',
        saving: 'Saving…',
        unsaved: 'Unsaved changes',
        saveError: 'Save error',
        saved: 'Saved',
        duplicate: 'Duplicate',
        projectActions: 'Project actions for {{name}}',
        delete: 'Delete',
        unpin: 'Unpin',
        pinTop: 'Pin to Top',
        removeFavorite: 'Remove from Favorites',
        addFavorite: 'Add to Favorites',
        editFolder: 'Edit Folder',
        moveRoot: 'Move to Root',
        deleteFolder: 'Delete Folder',
        dropInside: 'Drop inside',
        dropAbove: 'Drop above',
        dropBelow: 'Drop below',
        dragReorder: 'Drag to reorder',
        expand: 'Expand',
        collapse: 'Collapse',
        deleteTitle: 'Delete Project',
        deleteDescription: 'Are you sure you want to delete "{{name}}"? This action cannot be undone.',
        deleteFallback: 'Are you sure you want to delete this project?',
        sort: {
            recent: 'Recent',
            nameAsc: 'Name (A-Z)',
            nameDesc: 'Name (Z-A)',
            favorites: 'Favorites first',
            lastOpened: 'Last opened',
        },
        time: {
            justNow: 'Just now',
            minutesAgo: '{{count}}m ago',
            hoursAgo: '{{count}}h ago',
            daysAgo: '{{count}}d ago',
        },
    },
    help: {
        panelLabel: 'Help panel',
        title: 'Help & Shortcuts',
        close: 'Close help panel',
        shortcuts: 'Keyboard Shortcuts',
        quickTools: 'Quick Tools',
        tips: 'Tips',
        tutorial: 'Tutorial',
        restartTutorial: 'Restart guided tour',
        saveStatus: 'Save Status',
        save: 'Save',
        allSaved: 'All changes saved',
        saving: 'Saving…',
        unsaved: 'Unsaved changes',
        error: 'Save error',
        tools: {
            'zoom-fit': { label: 'Zoom Fit', description: 'Reset view to fit pitch' },
            'focus-mode': { label: 'Focus Mode', description: 'Minimal UI for focused work' },
            'print-mode': { label: 'Print Mode', description: 'Clean layout for export' },
            export: { label: 'Export', description: 'Export as PNG/PDF/GIF' },
        },
        tipsList: {
            'tip-1': 'Select a player and press → to add an arrow with number',
            'tip-2': 'Double-click a player to quickly edit their number',
            'tip-3': 'Hold Shift while clicking to multi-select elements',
            'tip-4': 'Press ? to see the full shortcut cheat sheet',
            'tip-5': 'Use Ctrl+Scroll to zoom in and out',
            'tip-6': 'Space + drag to pan the canvas',
        },
    },
    faq: {
        title: 'Help Center',
        subtitle: 'Find answers to common questions',
        search: 'Search help…',
        noResults: 'No matching questions found. Try a different search.',
        categories: {
            gettingStarted: 'Getting Started',
            proFeatures: 'Pro Features',
            clubPremium: 'Club Premium',
            troubleshooting: 'Troubleshooting',
            accountBilling: 'Account & Billing',
            coachingWorkflow: 'Coaching library & workflow',
        },
        items: {
            addPlayer: { question: 'How do I add a player?', answer: 'Press P to add a home team player, or Shift+P for an away team player. You can also right-click on the pitch and select "Add Player". Use number keys 1-6 for instant formations.' },
            addArrow: { question: 'How do I draw arrows?', answer: 'Press A for pass arrows (solid), R for run arrows (dashed), or S for shot arrows (double line). Click where you want the arrow to start, drag to the end point, and release. Press Shift+N for auto-numbering mode.' },
            saveProject: { question: 'How do I save my project?', answer: 'Press ⌘S (Ctrl+S on Windows) to save. Projects are auto-saved to the cloud every 30 seconds. Open the Projects panel (folder icon in TopBar) to view, rename, or organize all your projects.', cta: 'Save now' },
            freePlan: { question: 'What can I do for free?', answer: 'The Free plan includes three graphics, exercises and session plans each, 10 steps per project, PNG and JPG export, and cloud sync. Upgrade to Pro for an unlimited library.', cta: 'Compare plans' },
            exportGifPdf: { question: 'How do I export GIF or PDF?', answer: 'GIF and PDF export are Pro features. Click the Export button in the TopBar and select GIF Animation or PDF. If you don\'t see these options, you\'re on the Free plan.', cta: 'Upgrade to Pro' },
            unlimitedProjects: { question: 'Can I create unlimited projects?', answer: 'Unlimited projects and steps are available on the Pro plan. The Free plan allows three items of each type with 10 steps each.', cta: 'See Pro plans' },
            manageSubscription: { question: 'How do I manage my subscription?', answer: 'Click your avatar in the TopBar and select "Account & Billing". This opens the Stripe Customer Portal where you can update payment methods, view invoices, or change your plan.', cta: 'Open billing' },
            squadSlots: { question: 'How many squad players can I have?', answer: 'Free plan allows up to 5 squad players total. Pro and Club Premium unlock up to 35 players per team (140 total across 4 teams). Locked slots show a padlock icon.', cta: 'Upgrade for more slots' },
            addMembers: { question: 'How do I add team members?', answer: 'Open the Team settings, enter the member\'s email, and create an invitation link. Share that link with them; they receive Team access after accepting it with the invited email address.', cta: 'Open Team panel' },
            manageBilling: { question: 'How do I manage Club Premium billing?', answer: 'Click your avatar → "Account & Billing" to open the Stripe Customer Portal. Here you can update payment methods, view invoices, and manage your Club Premium subscription.', cta: 'Open billing' },
            removeMember: { question: 'How do I remove a team member?', answer: 'Open the Team panel in Inspector. Find the member you want to remove and click the trash icon next to their name. Confirm to revoke their Club Premium access immediately.' },
            saveFailed: { question: 'My project didn\'t save. What do I do?', answer: 'Check your internet connection first. If you\'re online, try a manual save with ⌘S (Ctrl+S on Windows). The error icon in the save status bar is clickable — it retries the save automatically.', cta: 'Manual save' },
            changesNotVisible: { question: 'I can\'t see my changes on the board', answer: 'Make sure the right layer is visible (Inspector → Layers tab). Check that you\'re on the correct step (bottom timeline). Try pressing 0 (zero) to reset the view, or check if you\'re in Print Mode (press W to toggle).' },
            exportNotWorking: { question: 'Export is not working', answer: 'For PNG export, check your browser permissions (some browsers block downloads). For GIF export, make sure you have at least 2 steps with different content. For PDF, ensure you\'re on the Pro plan.' },
            changePlan: { question: 'How do I change my plan?', answer: 'Click your avatar in the TopBar and select "Account & Billing" to manage your subscription, or open the Pricing modal to compare plans and upgrade.', cta: 'View plans' },
            cancelSubscription: { question: 'How do I cancel my subscription?', answer: 'Click your avatar → "Account & Billing" to open the Stripe Customer Portal. Select "Cancel plan" — your access continues until the end of the billing period.', cta: 'Go to billing' },
            guestRegister: { question: 'How do I create a free account?', answer: 'Click "Sign In" in the TopBar menu and select "Register". Use Google for one-click sign-up, or enter your email and create a password. Free stays free forever — no credit card needed.', cta: 'Sign up free' },
            lineupPresets: { question: 'How do I save and quickly load a team setup?', answer: 'Position players, open Settings → Squad and save a named setup such as Low block. Load it under the team name on the bench or with Option+1–9 on macOS and Alt+1–9 on Windows/Linux. The pencil opens quick editing.' },
            exercisesSessions: { question: 'How do I create an exercise and session plan?', answer: 'Create a Graphic in Projects, then an Exercise and attach that graphic. Add organization, coaching points and intervals. In a Session, order exercises, select squad and staff, then print or download the PDF.' },
            returnFromLineupEdit: { question: 'Where do I return after editing a preset?', answer: 'TMC Studio returns to where you started. Editing from Settings returns to Squad. Editing from the bench saves the preset and leaves you on the board.' },
        },
    },
    bottomSteps: {
        previous: 'Previous Step (←)',
        play: 'Play (Space)',
        pause: 'Pause (Space)',
        next: 'Next Step (→)',
        loop: 'Loop (L)',
        duration: 'Step duration (playback speed)',
        rename: 'Double-click to rename',
        delete: 'Delete step (X)',
        duplicate: 'Duplicate step',
        moveHint: 'Drag to reorder (Alt+←/→) · double-click to rename',
        add: 'Add Step (N)',
        counter: 'Step {{current}} / {{total}}',
        defaultName: 'Step {{number}}',
    },
    selection: {
        duplicate: 'Duplicate (⌘D)',
        bringFront: 'Bring to Front',
        sendBack: 'Send to Back',
        delete: 'Delete (Del)',
    },
    teamsPanel: {
        title: 'Team Colors',
        teamSuffix: '{{team}} Team',
        name: 'Name',
        primaryColor: 'Primary Color',
        secondaryColor: 'Secondary Color',
        goalkeeperColor: 'Goalkeeper Color',
        kitPresets: 'Kit presets',
        kits: {
            redWhite: 'Red & White',
            blueWhite: 'Blue & White',
            greenWhite: 'Green & White',
            blackWhite: 'Black & White',
            whiteBlack: 'White & Black',
            yellowBlue: 'Yellow & Blue',
            orangeBlack: 'Orange & Black',
            navySkyblue: 'Navy & Sky Blue',
        },
        help: 'Names and colors save automatically and apply immediately to all players.',
        team1: 'Team 1',
        team2: 'Team 2',
        team3: 'Team 3',
        team4: 'Team 4',
    },
    squadBench: {
        title: 'Squad Bench',
        home: 'Home',
        away: 'Away',
        team3: 'Team 3',
        team4: 'Team 4',
        empty: 'empty',
        add: 'add',
        addPlayer: 'Add player',
        addSquadPlayer: 'Add squad player',
        addFirstPlayer: 'Add first player',
        upgradeSlots: 'Upgrade to Pro for more squad slots',
        unlockSlots: 'Upgrade to Pro to unlock more squad slots',
        freeLimitNotice: 'Free mode allows {{freeLimit}} squad players. Sign in to save your roster; Pro unlocks up to {{premiumLimit}} players per team.',
        dragToPitch: '{{name}} (#{{number}}) — drag to pitch',
        playerAria: '{{name}}, number {{number}}, {{team}}{{locked}}',
        locked: ' — locked',
        remove: 'Remove {{name}}',
        removeAria: 'Remove {{name}} from squad',
        editor: 'Full squad editor in Settings',
        editRoster: 'Edit squad roster',
        hide: 'Hide squad bench',
        show: 'Show squad bench',
        switchCurrent: 'Switch team — current: {{team}}',
        currentTeam: 'Current team: {{team}}. Click to switch.',
        teamCount: '{{team}} ({{count}})',
        switchToTeam: 'Switch to {{team}} ({{count}} players)',
        namePlaceholder: 'Player',
        numberPlaceholder: '#',
        cancel: 'Cancel',
        dragHint: 'Drag a player onto the pitch to place them',
        collapsedSetup: 'Set up your squad in Settings or',
        collapsedCount: '{{count}} in squad — tap the eye to show',
        teams: 'Teams', tacticalLineups: 'Tactical lineups', chooseTacticalLineup: 'Choose lineup', editSelectedLineup: 'Selected lineup options', applySelectedLineup: 'Apply', editOnPitch: 'Edit on pitch', noTacticalLineups: 'No saved lineups for this team.', closeTeamMenu: 'Close team menu',
    },
    pitchPanel: {
        boardView: 'Board view',
        boardFull: 'Full pitch',
        boardHalf2d: 'Half pitch',
        boardPenalty2d: 'Penalty area',
        boardResetTitle: 'Reset board?',
        boardResetDesc: 'This removes all elements and restores the default setup. This cannot be undone.',
        boardResetConfirm: 'Reset board',
        title: 'Pitch Appearance',
        theme: 'Theme',
        grass: 'Grass',
        indoor: 'Indoor',
        chalk: 'Chalk',
        futsal: 'Futsal',
        showStripes: 'Show Stripes',
        withoutLines: 'Without Lines',
        printFriendly: 'Print Friendly',
        customColors: 'Custom Colors',
        fieldColor: 'Field Color',
        lineColor: 'Line Color',
        orientation: 'Orientation',
        landscape: 'Landscape',
        portrait: 'Portrait',
        help: 'Choose a preset theme or customize individual colors.',
    },
    tutorial: {
        finish: 'Finish',
        step: 'Step {{current}} of {{total}}',
        skip: 'Skip',
        back: 'Back',
        next: 'Next',
        startBuilding: 'Start coaching',
        ariaStep: 'Tutorial step {{id}}: {{title}}',
        demos: {
            fastInput: 'Quick add',
            homePlayer: 'Home player',
            awayPlayer: 'Away player',
            formation: 'Formation',
            arrows: 'Arrow types',
            passArrow: 'Pass',
            runArrow: 'Run',
            shootArrow: 'Shot',
            autoNumber: 'Auto-numbering',
            inspector: 'Inspector',
            output: 'Export formats',
            share: 'Share',
            projects: 'Projects',
            threePerType: '3 / type',
            cloud: 'Cloud',
            pro: 'Pro',
            squad: 'Squad roster',
            steps: 'Step timeline',
            play: 'Play',
            newStep: 'New step',
            loop: 'Loop',
            save: 'Save & projects',
            saveShortcut: 'Save',
            myProject: 'My training drill',
            justNow: 'Just now',
            saved: 'Saved',
            shortcutSheet: 'Shortcut sheet',
            team: 'Team management',
            admin: 'Admin',
            member: 'Member',
            active: 'Active',
            inviteMember: 'Invite member',
        },
        steps: {
            1: { eyebrow: '🏋️ Warm-up', title: 'Set up your players', description: 'Press P to add home players, Shift+P for away. Use number keys 1-6 for instant formations. Your tactical board starts here.', target: 'Add players', cta: 'Draw tactics' },
            2: { eyebrow: '🏃 Movement', title: 'Draw your tactics', description: 'Press A for pass arrows, R for runs, S for shots. Click-drag on the pitch to show exactly how your team should move.', target: 'Tactical arrows', cta: 'Set direction' },
            3: { eyebrow: '🧭 Direction', title: 'Show where players look', description: 'Select a player and press [ or ] to rotate them. Press V to show their vision cone — perfect for pressing drills and defensive shape.', target: 'Orientation', cta: 'Add equipment' },
            4: { eyebrow: '🏋️ Equipment', title: 'Build your drill', description: 'Add goals (J), cones (K), ladders (Y) and hurdles (U). Use Z for zones, T for text labels. Everything a coach needs for a session.', target: 'Drill equipment', cta: 'Manage squad' },
            5: { eyebrow: '📋 Squad', title: 'Your full roster at hand', description: 'The Squad Bench holds your players. Drag them onto the pitch to substitute or add new roles. Click the eye icon to toggle visibility.', target: 'Squad Bench', cta: 'Animate tactic' },
            6: { eyebrow: '▶️ Animation', title: 'Bring your tactic to life', description: 'Press N to add a new step, then adjust positions. Press Space to play — watch your drill animate step by step. Loop with L.', target: 'Step timeline', cta: 'Save project' },
            7: { eyebrow: '📚 Library', title: 'Turn drawings into sessions', description: 'Open Projects: save a board as a Graphic, turn it into an Exercise with coaching details, then add exercises to an ordered Session plan.', target: 'Coaching library', cta: 'Share & export' },
            8: { eyebrow: '📤 Share', title: 'Export and share with your team', description: 'Export your drill as PNG, GIF animation, or PDF. Press ? for the full shortcut sheet. Your tactical library is ready for matchday.', target: 'Export', cta: 'Start coaching' },
        },
    },
    cheatsheet: {
        title: 'Keyboard shortcuts',
        close: 'Close (or press ?)',
        closeShortcuts: 'Close Shortcuts (?)',
        keyboardShortcuts: 'Keyboard Shortcuts (?)',
        closeAria: 'Close keyboard shortcuts',
        showAria: 'Show keyboard shortcuts',
        categories: 'Shortcut categories',
        press: 'Press',
        toToggle: 'to toggle',
        tabs: { Elements: 'Elements', Edit: 'Edit', View: 'View', More: 'More' },
        sections: {
            'Elements': 'Elements', 'Edit': 'Edit', 'View & Pitch': 'View & Pitch',
            'Steps & Playback': 'Steps & Playback', 'Export': 'Export',
            'Formations': 'Formations', 'Arrow Numbering': 'Arrow Numbering',
        },
    },
    palette: {
        placeholder: 'Type a command or search…',
        noResults: 'No commands found',
        pro: 'Pro',
        navigate: 'navigate',
        select: 'select',
        close: 'close',
        categories: { elements: 'Elements', edit: 'Edit', view: 'View', steps: 'Steps', export: 'Export', presets: 'Presets' },
    },
    commands: {
        'add-home-player': 'Add Home Player',
        'add-away-player': 'Add Away Player',
        'add-ball': 'Add Ball',
        'add-pass-arrow': 'Add Pass Arrow',
        'add-run-arrow': 'Add Run Arrow',
        'add-shoot-arrow': 'Add Shot Arrow',
        'add-dribble-arrow': 'Add Dribble Arrow',
        'add-zone': 'Add Zone',
        'add-ellipse-zone': 'Add Ellipse Zone',
        'add-freehand-draw': 'Add Freehand Draw',
        'add-text': 'Add Text',
        'duplicate': 'Duplicate Selection',
        'delete': 'Delete Selection',
        'undo': 'Undo',
        'redo': 'Redo',
        'select-all': 'Select All',
        'clear-selection': 'Clear Selection',
        'toggle-inspector': 'Toggle Inspector',
        'toggle-cheatsheet': 'Toggle Shortcuts',
        'toggle-grid': 'Toggle Grid',
        'toggle-snap': 'Toggle Snap',
        'focus-mode': 'Focus Mode',
        'add-step': 'Add Step',
        'prev-step': 'Previous Step',
        'next-step': 'Next Step',
        'play': 'Play',
        'pause': 'Pause',
        'toggle-loop': 'Toggle Loop',
        'export-png': 'Export PNG',
        'export-steps': 'Export All Steps PNG',
        'export-gif': 'Export Animated GIF',
        'export-pdf': 'Export PDF (all steps)',
        'export-svg': 'Export SVG',
        confirm: {
            clearAllTitle: 'Clear All Elements?',
            clearAllDescription: 'This will remove all elements from the current step. You can undo this action with Cmd+Z.',
            clearAllConfirm: 'Clear All',
        },
        toast: {
            groupUngrouped: 'Group ungrouped',
            selectionLockToggled: 'Lock toggled',
            selectionLocked: 'Selection locked',
            selectionUnlocked: 'Selection unlocked',
            team1Player: 'Team 1 Player',
            team2Player: 'Team 2 Player',
            team3Player: 'Team 3 Player',
            team4Player: 'Team 4 Player',
            ballCluster: 'Ball cluster',
            ball: 'Ball',
            text: 'Text',
            lyingMannequin: 'Lying mannequin',
            mannequin: 'Mannequin',
            discMarker: 'Disc marker',
            pole: 'Pole',
            cone: 'Cone',
            hoop: 'Hoop',
            hurdle: 'Hurdle',
            ladder: 'Ladder',
            miniGoal: 'Mini goal',
            goal: 'Goal',
            redo: 'Redone',
            undo: 'Undone',
            deleted: 'Deleted',
            duplicated: 'Duplicated',
            dribbleArrow: 'Dribble arrow',
            copied: 'Copied',
            allElementsCleared: 'All elements cleared',
            noDrawingsToClear: 'No drawings to clear',
            drawingsCleared: '{{count}} drawing(s) cleared · Undo: Cmd+Z',
            pasted: 'Pasted',
            enableOrientation: 'Enable player orientation first (Inspector → Orientation)',
            orientationModeOn: 'Player orientation mode: ON',
            orientationModeOff: 'Player orientation mode: OFF',
            visionAllOn: 'Vision: ON — {{count}} player(s)',
            visionAllOff: 'Vision: OFF — {{count}} player(s)',
            noPlayersOnBoard: 'No players on board',
            visionSelected: 'Vision toggled for {{count}} player(s)',
            visionSelectedOn: 'Vision enabled for {{count}} player(s)',
            selectPlayersForVision: 'Select player(s) to toggle vision',
            groupCreated: 'Group created',
            goalkeeperSet: '{{team}} goalkeeper set',
            goalkeeperColor: '{{team}} GK color: {{color}}',
            gridHidden: 'Grid hidden',
            gridVisible: 'Grid visible',
            snapEnabled: 'Snap enabled',
            snapDisabled: 'Snap disabled',
            passArrowAutoNumber: 'Pass arrow (auto-number)',
            runArrowAutoNumber: 'Run arrow (auto-number)',
            zoneShapeChanged: 'Zone shape changed',
            savedCloud: 'Saved to cloud',
            cloudSaveFailed: 'Cloud save failed',
            savedLocal: 'Saved locally',
            shapeChanged: 'Shape changed',
            portraitMode: 'Portrait mode (75%)',
            landscapeMode: 'Landscape mode',
            printModeOff: 'Print Mode OFF',
            printModeOn: 'Print Mode ON',
            stepAdded: 'New step added',
            stepDeleted: 'Step deleted',
            stepLimitFree: 'You have {{count}}/10 steps. Upgrade to Pro for unlimited.',
            loopOn: 'Loop enabled',
            loopOff: 'Loop disabled',
            autoNumberingOn: 'Auto-numbering ON',
            autoNumberingOff: 'Auto-numbering OFF',
            background: 'Background: {{color}}',
            backgroundRemoved: 'Background removed',
            signInToSave: 'Sign in to save projects — it\'s free',
            fontSize: 'Font size: {{size}}px',
            previousColor: 'Previous color',
            nextColor: 'Next color',
            normal: 'Normal',
            bold: 'Bold',
            italic: 'Italic',
            thinnerStroke: 'Thinner stroke',
            thickerStroke: 'Thicker stroke',
            alignPrev: 'Previous alignment',
            alignNext: 'Next alignment',
            arrowNumberToggled: 'Arrow number toggled',
            resizedUp: 'Resized +10%',
            resizedDown: 'Resized -10%',
            resizedTo: 'Resized to {{percent}}%',
            layerControlSoon: 'Layer control coming soon',
            playerAdded: 'Player added',
            ballAdded: 'Ball added',
            arrowAdded: 'Arrow added',
            zoneAdded: 'Zone added',
            colorChanged: 'Color changed',
            switchedTeam: 'Switched to {{team}}',
            playerColorChanged: 'Player color changed (use Alt+↓/↑ to cycle)',
            textColorChanged: 'Text color changed (use Alt+↓/↑ to cycle)',
            equipmentUp: 'Equipment +15%',
            equipmentDown: 'Equipment -15%',
            orientationReset: 'Player orientation reset',
            playerOrientation: 'Player orientation {{delta}}°',
            rotatedLeft15: 'Rotated -15°',
            rotatedRight15: 'Rotated +15°',
            rotatedLeft90: 'Rotated -90°',
            rotatedRight90: 'Rotated +90°',
            formationApplied: '{{formation}} applied ({{team}})',
            lineupApplied: 'Saved lineup {{slot}} applied',
            lineupEmpty: 'Lineup slot {{slot}} is empty',
        },
    },
    shortcuts: {
        'add-home-player': 'Add Home Player',
        'add-away-player': 'Add Away Player',
        'add-team3-player': 'Add Team 3 Player',
        'add-team4-player': 'Add Team 4 Player',
        'add-ball': 'Add Ball',
        'pass-arrow': 'Pass Arrow',
        'run-arrow': 'Run Arrow',
        'shot-arrow': 'Shot Arrow',
        'dribble-arrow': 'Dribble Arrow',
        'rect-zone': 'Rect Zone',
        'ellipse-zone': 'Ellipse Zone',
        'text-label': 'Text Label',
        'freehand-draw': 'Freehand Draw',
        'highlighter': 'Highlighter',
        'clear-drawings': 'Clear Drawings',
        'goal': 'Goal (Shift Mini)',
        'mannequin': 'Mannequin (Shift Flat)',
        'cone': 'Cone (Shift Pole)',
        'ladder': 'Ladder',
        'hoop': 'Hoop',
        'hurdle': 'Hurdle',
        'rotate-minus': 'Rotate -15°',
        'rotate-plus': 'Rotate +15°',
        'rotate-minus-90': 'Rotate -90°',
        'rotate-plus-90': 'Rotate +90°',
        'undo': 'Undo',
        'redo': 'Redo',
        'copy': 'Copy',
        'paste': 'Paste',
        'duplicate': 'Duplicate',
        'delete': 'Delete',
        'deselect': 'Clear Selection',
        'select-all': 'Select All',
        'cycle-player-shape': 'Cycle Player Shape',
        'cycle-zone-shape': 'Cycle Zone Shape',
        'group-selection': 'Group Selection',
        'ungroup-selection': 'Ungroup Selection',
        'lock-unlock': 'Lock / Unlock Selection',
        'quick-edit-number': 'Quick Edit Number',
        'cycle-color': 'Cycle Color',
        'stroke-width': 'Stroke Width',
        'focus-mode': 'Focus Mode',
        'toggle-inspector': 'Toggle Inspector',
        'player-orientation': 'Player Orientation',
        'print-mode': 'Print Mode',
        'cycle-pitch-view': 'Cycle Pitch View',
        'all-shortcuts': 'All Shortcuts',
        'command-palette': 'Command Palette',
        'toggle-orientation': 'Toggle Orientation',
        'toggle-orientation-mode': 'Toggle Player Orientation Mode',
        'open-projects': 'Open Projects',
        'zoom-in': 'Zoom In',
        'zoom-out': 'Zoom Out',
        'toggle-shortcuts': 'Toggle Shortcuts',
        'prev-next-step': 'Prev/Next Step',
        'play-pause': 'Play/Pause',
        'new-step': 'New Step',
        'delete-step': 'Delete Step',
        'add-step': 'Add Step',
        'toggle-loop': 'Toggle Loop',
        'export-png': 'Export PNG',
        'export-all-png': 'Export All PNGs',
        'export-gif': 'Export GIF',
        'export-pdf': 'Export PDF',
        'home-formation': 'Apply Home Formation',
        'away-formation': 'Apply Away Formation',
        'set-gk': 'Set GK / cycle GK color',
        'pass-num': 'Pass + Num',
        'run-num': 'Run + Num',
        'toggle-auto-num': 'Toggle Auto-Num',
        'toggle-number': 'Toggle Number',
    },
    appToast: {
        clubCreated: 'Club created',
        upgradeSuccessful: 'Upgrade successful.',
        subscriptionActivating: 'Your subscription is being activated. Refresh in a moment.',
        subscriptionFree: 'Subscription updated — you are now on Free.',
        subscriptionPro: 'Subscription updated — Pro is active.',
        subscriptionTeam: 'Subscription updated — Team is active.',
        billingUpdated: 'Billing updated.',
        checkoutCancelled: 'Checkout cancelled',
        welcomeBack: 'Welcome back.',
        googleLoginStarted: 'Google sign-in opened in a separate window.',
        googleLoginFailed: 'Google sign-in failed. Please try again.',
        devLogin: 'Logged in as test {{tier}} user',
    },
    storeToast: {
        toolActive: '{{tool}} tool active — click to place • Esc to exit',
        zoom: 'Zoom: {{percent}}%',
        zoomFit: 'Zoom: Fit',
        backOnline: 'Back online',
        offline: 'You are offline',
        saveFailedRetry: 'Save failed — will retry when online',
        saveGuestTitle: 'Save Your Work?',
        saveGuestDescription: 'You have unsaved work from your guest session. Would you like to save it to your cloud account?',
        saveGuestConfirm: 'Save to Cloud',
        discard: 'Discard',
        guestWorkSaved: 'Your work has been saved to the cloud.',
        guestWorkSaveFailed: 'Failed to save. Try Cmd+S to save manually.',
        tools: {
            'player-home': 'Home Player',
            'player-away': 'Away Player',
            ball: 'Ball',
            'arrow-pass': 'Pass Arrow',
            'arrow-run': 'Run Arrow',
            'arrow-shoot': 'Shoot Arrow',
            'arrow-dribble': 'Dribble Arrow',
            zone: 'Zone',
            'zone-ellipse': 'Ellipse Zone',
            'zone-polygon': 'Polygon Zone — click to add points, double-click to finish',
            text: 'Text',
        },
    },
    legal: {
        draftBanner: '⚠ This is a draft — waiting for legal review.',
        updated: 'Last updated: {{date}}',
        back: 'Back to TMC Studio',
        privacy: {
            title: 'Privacy Policy',
            intro: {
                title: '1. Introduction',
                body: 'TMC Studio ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our tactical board application.',
            },
            collect: {
                title: '2. Information We Collect',
                providedTitle: '2.1 Information You Provide',
                automaticTitle: '2.2 Automatically Collected Information',
                provided: {
                    account: 'Account information (email, name)',
                    project: 'Project data (tactical boards, formations, annotations)',
                    payment: 'Payment information (processed securely by Stripe)',
                },
                automatic: {
                    usage: 'Usage data and analytics',
                    device: 'Device and browser information',
                    location: 'IP address and location data',
                },
            },
            use: {
                title: '3. How We Use Your Information',
                items: {
                    maintain: 'Provide and maintain our service',
                    payments: 'Process your payments and subscriptions',
                    updates: 'Send you updates and notifications',
                    improve: 'Improve our application and user experience',
                    legal: 'Comply with legal obligations',
                },
            },
            storage: {
                title: '4. Data Storage and Security',
                body: 'Your data is stored securely using Supabase (PostgreSQL database) with encryption at rest and in transit. We implement industry-standard security measures to protect your information.',
            },
            sharing: {
                title: '5. Data Sharing',
                body: 'We do not sell your personal information. We may share your data with:',
                stripe: 'for payment processing',
                supabase: 'for data hosting and authentication',
                netlify: 'for application hosting',
                sentry: 'for error monitoring (technical error reports, without the content of your boards)',
                postmark: 'for sending transactional e-mails (club invitations, replies to bug reports)',
                google: 'for signing in with a Google account, if you choose it',
                plausible: 'for privacy-friendly usage statistics, only after you consent to analytics',
            },
            rights: {
                title: '6. Your Rights',
                body: 'You have the right to:',
                items: {
                    access: 'Access your personal data',
                    correct: 'Correct inaccurate data',
                    delete: 'Request deletion of your data',
                    export: 'Export your data',
                    optOut: 'Opt out of marketing communications',
                },
            },
            cookies: {
                title: '7. Cookies',
                before: 'We use essential cookies for authentication and preference storage. You can control cookies through your browser settings. See our',
                link: 'Cookie Policy',
                after: 'for more details.',
            },
            children: {
                title: "8. Children's Privacy",
                body: 'Our service is not intended for children under 13. We do not knowingly collect personal information from children.',
            },
            changes: {
                title: '9. Changes to This Policy',
                body: 'We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new policy on this page and updating the "Last updated" date.',
            },
            contact: {
                title: '10. Contact Us',
                body: 'If you have any questions about this Privacy Policy, please contact us at:',
            },
        },
        terms: {
            title: 'Terms of Service',
            acceptance: {
                title: '1. Acceptance of Terms',
                body: 'By accessing and using TMC Studio ("Service"), you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to these Terms of Service, please do not use our Service.',
            },
            description: {
                title: '2. Description of Service',
                body: 'TMC Studio is a web-based tactical board application for sports coaches and analysts. The Service allows users to create, edit, and share tactical diagrams, formations, and animated sequences.',
            },
            accounts: {
                title: '3. User Accounts',
                registrationTitle: '3.1 Registration',
                registration: 'To access certain features, you must create an account. You agree to provide accurate, current, and complete information during registration.',
                securityTitle: '3.2 Account Security',
                security: 'You are responsible for maintaining the confidentiality of your account credentials. You agree to notify us immediately of any unauthorized use of your account.',
                terminationTitle: '3.3 Account Termination',
                termination: 'We reserve the right to suspend or terminate your account if you violate these Terms or engage in fraudulent or illegal activity.',
            },
            payments: {
                title: '4. Subscription and Payments',
                pricingTitle: '4.1 Pricing',
                pricing: 'We offer both free and paid subscription plans. Pricing is displayed on our website and may be changed with 30 days notice.',
                billingTitle: '4.2 Billing',
                billing: 'Paid subscriptions are billed monthly or annually in advance. Payments are processed securely through Stripe.',
                refundsTitle: '4.3 Refunds',
                refunds: 'Refund requests are handled on a case-by-case basis. Please contact support if you believe you are entitled to a refund.',
                cancellationTitle: '4.4 Cancellation',
                cancellation: 'You may cancel your subscription at any time. Cancellations take effect at the end of your current billing period.',
            },
            content: {
                title: '5. User Content',
                ownershipTitle: '5.1 Ownership',
                ownership: 'You retain all rights to the tactical boards and content you create using our Service. We do not claim ownership of your user content.',
                licenseTitle: '5.2 License',
                license: 'By using our Service, you grant us a limited license to host, store, and display your content solely for the purpose of providing the Service to you.',
                prohibitedTitle: '5.3 Prohibited Content',
                prohibitedIntro: 'You agree not to upload or share content that:',
                prohibited: {
                    laws: 'Violates any laws or regulations',
                    ip: 'Infringes on intellectual property rights',
                    malware: 'Contains malware or harmful code',
                    offensive: 'Is offensive, harassing, or discriminatory',
                },
            },
            acceptable: {
                title: '6. Acceptable Use',
                intro: 'You agree not to:',
                items: {
                    reverse: 'Reverse engineer or attempt to extract source code',
                    automated: 'Use automated tools to access the Service (bots, scrapers)',
                    disrupt: 'Interfere with or disrupt the Service',
                    share: 'Share your account with others',
                    illegal: 'Use the Service for any illegal purpose',
                },
            },
            ip: {
                title: '7. Intellectual Property',
                body: 'The Service, including its software, design, graphics, and content (excluding user content), is owned by TMC Studio and protected by copyright, trademark, and other intellectual property laws.',
            },
            disclaimers: {
                title: '8. Disclaimers',
                body: 'THE SERVICE IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND. We do not guarantee that the Service will be uninterrupted, secure, or error-free.',
            },
            liability: {
                title: '9. Limitation of Liability',
                body: 'TO THE MAXIMUM EXTENT PERMITTED BY LAW, TMC Studio shall not be liable for any indirect, incidental, special, or consequential damages arising from your use of the Service.',
            },
            backup: {
                title: '10. Data Backup',
                body: 'While we implement backup procedures, you are responsible for maintaining your own backups of your content. We are not liable for data loss.',
            },
            changes: {
                title: '11. Changes to Terms',
                body: 'We reserve the right to modify these Terms at any time. We will notify users of significant changes via email or in-app notification. Continued use of the Service constitutes acceptance of modified Terms.',
            },
            law: {
                title: '12. Governing Law',
                body: 'These Terms shall be governed by and construed in accordance with the laws of Poland, without regard to conflict of law provisions.',
            },
            contact: {
                title: '13. Contact Information',
                body: 'If you have any questions about these Terms, please contact us at:',
            },
        },
        cookies: {
            title: 'Cookie Policy',
            what: {
                title: '1. What Are Cookies?',
                body: 'Cookies are small text files that are placed on your device when you visit our website. They help us provide you with a better experience by remembering your preferences and understanding how you use our Service.',
            },
            use: {
                title: '2. How We Use Cookies',
                body: 'TMC Studio uses cookies for the following purposes:',
            },
            types: {
                title: '3. Types of Cookies We Use',
                essentialTitle: '3.1 Essential Cookies',
                essentialBody: 'These cookies are necessary for the Service to function properly. They cannot be disabled.',
                essential: {
                    authentication: { label: 'Authentication', body: 'Keeps you signed in to your account' },
                    session: { label: 'Session Management', body: 'Maintains your session while you use the app' },
                    security: { label: 'Security', body: 'Protects against CSRF attacks and ensures secure communication' },
                },
                preferenceTitle: '3.2 Preference Cookies',
                preferenceBody: 'These cookies remember your settings and preferences:',
                preference: {
                    theme: { label: 'Theme', body: 'Remembers your dark/light mode preference' },
                    language: { label: 'Language', body: 'Stores your language selection' },
                    ui: { label: 'UI Settings', body: 'Saves inspector panel state, zoom level, etc.' },
                },
                analyticsTitle: '3.3 Analytics Cookies (Optional)',
                analyticsBody: 'These cookies help us understand how visitors use our Service:',
                analytics: {
                    usage: { label: 'Usage Patterns', body: 'Which features are most popular' },
                    performance: { label: 'Performance', body: 'How fast pages load' },
                    errors: { label: 'Error Tracking', body: 'Identifies technical issues' },
                },
                analyticsNote: 'With your consent, we use privacy-focused Plausible analytics to measure product usage and errors. We do not build advertising profiles.',
            },
            thirdParty: {
                title: '4. Third-Party Cookies',
                body: 'We use the following third-party services that may set cookies:',
                supabase: 'for authentication and data storage',
                stripe: 'for secure payment processing',
                policies: 'These services have their own privacy and cookie policies:',
                supabasePolicy: 'Supabase Privacy Policy',
                stripePolicy: 'Stripe Privacy Policy',
            },
            localStorage: {
                title: '5. Local Storage',
                body: 'In addition to cookies, we use browser local storage to:',
                items: {
                    cache: 'Cache your tactical boards for offline access',
                    state: 'Store application state between sessions',
                    project: 'Remember your last edited project',
                },
            },
            manage: {
                title: '6. Managing Cookies',
                browserTitle: '6.1 Browser Controls',
                browserBody: 'Most browsers allow you to control cookies through their settings. You can:',
                browser: {
                    blockAll: 'Block all cookies',
                    blockThirdParty: 'Block third-party cookies only',
                    deleteSession: 'Delete cookies after each session',
                    viewDelete: 'View and delete specific cookies',
                },
                impactTitle: '6.2 Impact of Blocking Cookies',
                important: 'Important:',
                impactBody: 'If you block essential cookies, you will not be able to sign in or use cloud features. Preference cookies can be blocked with minimal impact on functionality.',
                changeConsent: 'Change analytics consent',
            },
            duration: {
                title: '7. Cookie Duration',
                sessionTitle: 'Session Cookies',
                sessionBody: 'Deleted when you close your browser',
                persistentTitle: 'Persistent Cookies',
                persistentBody: 'Remain for up to 1 year, or until you delete them',
            },
            updates: {
                title: '8. Updates to This Policy',
                body: 'We may update this Cookie Policy from time to time. We will notify you of any significant changes by updating the "Last updated" date at the top of this page.',
            },
            contact: {
                title: '9. Contact Us',
                body: 'If you have questions about our use of cookies, please contact us at:',
            },
        },
        refunds: {
            title: 'Refunds and cancellation',
            intro: 'This page explains cancellation, withdrawal rights and payment support for TMC Studio subscriptions.',
            withdrawalTitle: 'Right of withdrawal',
            withdrawalBody: 'If you contract as a consumer in the European Union, you may have a 14-day right of withdrawal unless you consent to the digital service starting before that period ends.',
            waiverTitle: 'Starting the digital service',
            waiverBody: 'Paid features may become available immediately after purchase. In that case, under applicable law, withdrawal rights may expire once the service starts with your consent.',
            cancelTitle: 'Cancelling a subscription',
            cancelBody: 'You can cancel your subscription at any time from account and billing settings. Paid access remains active until the end of the already paid billing period.',
            refundTitle: 'Refunds',
            refundBody: 'Refunds are reviewed case by case, especially for incorrect charges, technical issues or duplicate payments. Payments are processed by Stripe.',
            contactTitle: 'Payment support',
            contactBody: 'Contact us if you need help with cancellation, invoices or refunds:',
        },
        legalNotice: {
            title: 'Legal notice',
            intro: 'Information about the entity responsible for TMC Studio and official contact details.',
            providerTitle: 'Service provider',
            registeredAt: 'Registration date: {{date}}',
            contactTitle: 'Contact',
            contactBody: 'For legal and formal matters, contact us at:',
            vatTitle: 'Tax identification',
            vatBody: 'Tax ID/NIP: {{nip}}. Online payments are processed by Stripe.',
            responsibleTitle: 'Responsibility for content',
            responsibleBody: 'We work to keep information on the service accurate and up to date. User-created content remains the responsibility of its authors.',
        },
        accessibility: {
            title: 'Accessibility',
            commitmentTitle: 'Our commitment',
            commitmentBody: 'We want TMC Studio to be as accessible as possible for coaches, analysts and creators using different devices and assistive technologies.',
            statusTitle: 'Current status',
            statusBody: 'The app is under active development. We are improving interface readability, keyboard support, contrast and layout stability on smaller screens.',
            feedbackTitle: 'Feedback',
            feedbackBody: 'If you notice an accessibility barrier or anything that makes the app harder to use, contact us:',
        },
    },
    organizationPanel: {
        title: 'Club',
        seats: 'Seats used: {{used}} / {{max}}',
        upsell: {
            description: 'Invite coaches and staff to your club by upgrading to the Team plan.',
            cta: 'Upgrade to Team',
        },
        intro: {
            title: 'How it works',
            step1: '1. Create your club and give it a name.',
            step2: '2. Create a secure invitation link and share it with your staff.',
            step3: '3. Every member gets access to the club\'s shared projects.',
        },
        create: {
            description: 'Create your club to invite coaches and staff members.',
            nameLabel: 'Club name',
            namePlaceholder: 'e.g. FC Example Youth Academy',
            cta: 'Create club',
            creating: 'Creating…',
        },
        members: {
            title: 'Members',
            remove: 'Remove',
            leave: 'Leave club',
            leaving: 'Leaving…',
            transferOwnership: 'Transfer ownership',
            transferSelect: 'Choose a new owner…',
            transferConfirm: 'Transfer',
            transferring: 'Transferring…',
            cancel: 'Cancel',
            deleteClub: 'Delete club',
            deleteClubConfirm: 'Delete this club permanently? This cannot be undone.',
            deleteClubConfirmCta: 'Yes, delete',
            deleting: 'Deleting…',
        },
        invite: {
            title: 'Invite',
            emailPlaceholder: 'coach@example.com',
            cta: 'Send invite',
            sending: 'Sending…',
            pending: 'Pending invitations',
            copyLink: 'Copy invite link',
            linkCopied: 'Link copied!',
            revoke: 'Revoke',
            seatLimitReached: 'You have reached your seat limit for the Team plan. Remove a member or upgrade to invite more people.',
            shareHint: 'TMC Studio sends a secure invitation email. You can still copy the link from pending invitations.',
            sent: 'Invitation sent to {{email}}.',
        },
        roles: {
            owner: 'Owner',
            member: 'Member',
        },
        memberAccess: {
            title: 'You are a member of {{club}}',
            description: 'You have access to this club\'s shared projects and resources.',
        },
        errors: {
            createFailed: 'Could not create the club. Please try again.',
            inviteFailed: 'Could not send the invitation. Please try again.',
            invalidRequest: 'The invitation request is invalid.',
            invalidEmail: 'Enter a valid email address.',
            notAuthorized: 'Only the club owner can send invitations.',
            notAuthenticated: 'Sign in again before sending an invitation.',
            teamRequired: 'An active Team plan is required to invite members.',
            seatLimitReached: 'The Team seat limit has been reached.',
            alreadyMember: 'This person is already a club member.',
            alreadyPending: 'An invitation is already pending for this email.',
            deliveryFailed: 'The email could not be delivered. No pending invitation was created.',
            configurationError: 'Invitation email is temporarily unavailable.',
            rateLimited: 'Too many invitations were sent. Wait a minute and try again.',
            transferFailed: 'Could not transfer ownership. Please try again.',
            deleteFailed: 'Could not delete the club. Please try again.',
            leaveFailed: 'Could not leave the club. Please try again.',
        },
        welcome: {
            title: 'Welcome to Club Premium!',
            subtitle: 'Set up your club in 3 quick steps',
            nameTeamLabel: 'Name your club',
            nameTeamPlaceholder: 'e.g. FC Example Youth Academy',
            continue: 'Continue',
            skip: 'I\'ll do this later',
            inviteTitle: 'Invite your first member',
            inviteDescription: 'Open the Team panel to invite coaches and staff to your club.',
            teamPanelHint: 'Open Team panel in Inspector',
            teamPanelDesc: 'Add members by email — they get instant Club Premium access',
            openTeamPanel: 'Open Team panel',
            doLater: 'I\'ll invite later',
            completeTitle: 'You\'re all set!',
            completeDescription: 'Your club is ready. Here\'s what you can do now:',
            benefitUnlimited: 'Unlimited projects and steps',
            benefitExport: 'Export GIF, PDF, and more',
            benefitMembers: 'Manage up to 5 team members',
            startCoaching: 'Start coaching',
        },
    },
    invitePage: {
        loading: 'Loading invitation…',
        goToApp: 'Go to TMC Studio',
        notFound: {
            title: 'Invitation not found',
            description: 'This invitation link is invalid. Please ask the club admin to send a new one.',
        },
        expired: {
            title: 'Invitation no longer valid',
            description: 'This invitation has expired or was revoked. Please ask the club admin to send a new one.',
        },
        declined: {
            title: 'Invitation declined',
            description: 'You declined the invitation to {{club}}. The club admin can send a new one if you change your mind.',
        },
        accepted: {
            title: "You're in!",
            description: 'You have joined {{club}}.',
        },
        ready: {
            title: "You've been invited",
            description: 'You have been invited to join {{club}} as {{role}} ({{email}}).',
            signInPrompt: 'Sign in or create an account with {{email}} to accept this invitation.',
            signIn: 'Sign in',
            wrongAccount: 'This invitation was sent to {{invited}}, but you are signed in as {{current}}. Please sign in with the invited email address.',
            accept: 'Accept invitation',
            accepting: 'Joining…',
            decline: 'Decline invitation',
            declining: 'Declining…',
        },
        errors: {
            acceptFailed: 'Could not accept the invitation. Please try again.',
            declineFailed: 'Could not decline the invitation. Please try again.',
        },
    },
    languages: {
        pl: 'Polski',
        en: 'English',
        es: 'Español',
    },
    pageNotFound: {
        title: 'Page not found',
        description: 'This address does not exist or has been moved.',
        cta: 'Open the board',
    },
};
//# sourceMappingURL=en.js.map